import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/backend/lib/firebase';
import { snapCheckIn } from '@/shared/utils/monthRange';
import type { AvailableRoom } from '@/shared/types';

const getAvailableRoomsFn = httpsCallable<{ checkIn: string }, AvailableRoom[]>(
  functions,
  'getAvailableRooms'
);

export const useAvailableRooms = (checkIn: Date | undefined) => {
  // Bookings persist as whole months, so availability must be answered for the
  // snapped month window — the student may pick any exact day, but the claim
  // starts on the 1st of that month.
  const snapped = checkIn ? snapCheckIn(checkIn) : undefined;

  return useQuery({
    queryKey: ['availableRooms', snapped?.toISOString()],
    queryFn: async (): Promise<AvailableRoom[]> => {
      const result = await getAvailableRoomsFn({ checkIn: snapped!.toISOString() });
      return result.data;
    },
    enabled: !!snapped,
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Keep the previous date's rooms rendered while a new date is fetching, so
    // changing the move-in date doesn't blank the list to skeletons.
    placeholderData: keepPreviousData,
  });
};
